/**
 * VehicleForm - Edit vehicle details
 *
 * Form for creating/updating vehicle information with progressive disclosure.
 * Uses Catalyst form components.
 */

import { useState } from 'react';

import { Button } from '@/components/ui/catalyst/button';
import {
  Field,
  FieldGroup,
  Fieldset,
  Label,
} from '@/components/ui/catalyst/fieldset';
import { Input } from '@/components/ui/catalyst/input';
import { Select } from '@/components/ui/catalyst/select';
import { Textarea } from '@/components/ui/catalyst/textarea';
import type {
  CreateVehicle,
  ReadVehicle,
  UpdateVehicle,
} from '@/services/api/subjectsService';

interface VehicleFormProps {
  vehicle?: ReadVehicle;
  matterId: string;
  onSubmit: (data: CreateVehicle | UpdateVehicle) => void;
  onCancel: () => void;
  onDelete?: (id: string) => void;
}

export const VehicleForm = ({
  vehicle,
  matterId,
  onSubmit,
  onCancel,
  onDelete,
}: VehicleFormProps) => {
  const [formData, setFormData] = useState<
    Partial<CreateVehicle | UpdateVehicle>
  >({
    make: vehicle?.make || '',
    model: vehicle?.model || '',
    year: vehicle?.year || undefined,
    color: vehicle?.color || '',
    vehicle_type: vehicle?.vehicle_type || 'passenger_car',
    vin: vehicle?.vin || '',
    license_plate: vehicle?.license_plate || '',
    license_plate_state_code: vehicle?.license_plate_state_code || '',
    estimated_speed_mph: vehicle?.estimated_speed_mph || undefined,
    label_in_police_report: vehicle?.label_in_police_report || '',
    involvement_description: vehicle?.involvement_description || '',
  });

  const [showAdvanced, setShowAdvanced] = useState(false);

  const handleChange = (field: string, value: string | number) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const payload = vehicle
      ? ({ ...formData, id: vehicle.id } as UpdateVehicle)
      : ({ ...formData, matter_id: matterId } as CreateVehicle);
    onSubmit(payload);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <Fieldset>
        <FieldGroup>
          {/* Vehicle identification */}
          <div className="grid grid-cols-2 gap-4">
            <Field>
              <Label>Year</Label>
              <Input
                type="number"
                value={formData.year || ''}
                onChange={(e) =>
                  handleChange('year', parseInt(e.target.value, 10))
                }
                placeholder="2020"
              />
            </Field>
            <Field>
              <Label>Make</Label>
              <Input
                type="text"
                value={formData.make || ''}
                onChange={(e) => handleChange('make', e.target.value)}
                placeholder="Toyota"
              />
            </Field>
            <Field>
              <Label>Model</Label>
              <Input
                type="text"
                value={formData.model || ''}
                onChange={(e) => handleChange('model', e.target.value)}
                placeholder="Camry"
              />
            </Field>
            <Field>
              <Label>Color</Label>
              <Input
                type="text"
                value={formData.color || ''}
                onChange={(e) => handleChange('color', e.target.value)}
                placeholder="Red"
              />
            </Field>
          </div>

          {/* Vehicle type */}
          <Field>
            <Label>Vehicle Type</Label>
            <Select
              value={formData.vehicle_type || 'passenger_car'}
              onChange={(e) => handleChange('vehicle_type', e.target.value)}
            >
              <option value="passenger_car">Passenger Car</option>
              <option value="truck">Truck</option>
              <option value="motorcycle">Motorcycle</option>
              <option value="bus">Bus</option>
              <option value="bicycle">Bicycle</option>
              <option value="scooter">Scooter</option>
              <option value="skateboard">Skateboard</option>
              <option value="roller_skate">Roller Skate</option>
              <option value="roller_blade">Roller Blade</option>
              <option value="trailer">Trailer</option>
              <option value="other">Other</option>
            </Select>
          </Field>

          {/* License plate */}
          <div className="grid grid-cols-2 gap-4">
            <Field>
              <Label>License Plate</Label>
              <Input
                type="text"
                value={formData.license_plate || ''}
                onChange={(e) => handleChange('license_plate', e.target.value)}
              />
            </Field>
            <Field>
              <Label>State</Label>
              <Input
                type="text"
                value={formData.license_plate_state_code || ''}
                onChange={(e) =>
                  handleChange('license_plate_state_code', e.target.value)
                }
                maxLength={2}
                placeholder="CA"
              />
            </Field>
          </div>

          {/* Involvement description */}
          <Field>
            <Label>Involvement</Label>
            <Textarea
              value={formData.involvement_description || ''}
              onChange={(e) =>
                handleChange('involvement_description', e.target.value)
              }
              placeholder="Brief description of how this vehicle was involved in the incident"
              rows={2}
            />
          </Field>

          {/* Advanced fields - progressive disclosure */}
          {showAdvanced && (
            <>
              <Field>
                <Label>VIN</Label>
                <Input
                  type="text"
                  value={formData.vin || ''}
                  onChange={(e) => handleChange('vin', e.target.value)}
                  placeholder="17-character VIN"
                  maxLength={17}
                />
              </Field>

              <Field>
                <Label>Estimated Speed (MPH)</Label>
                <Input
                  type="number"
                  value={formData.estimated_speed_mph || ''}
                  onChange={(e) =>
                    handleChange(
                      'estimated_speed_mph',
                      parseInt(e.target.value, 10),
                    )
                  }
                />
              </Field>

              <Field>
                <Label>Police Report Label</Label>
                <Input
                  type="text"
                  value={formData.label_in_police_report || ''}
                  onChange={(e) =>
                    handleChange('label_in_police_report', e.target.value)
                  }
                  placeholder="Vehicle 1, Vehicle 2, etc."
                />
              </Field>
            </>
          )}

          {/* Toggle advanced fields */}
          <button
            type="button"
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="text-sm text-gray-600 hover:text-gray-900"
          >
            {showAdvanced ? 'Hide' : 'Show'} additional fields
          </button>
        </FieldGroup>
      </Fieldset>

      {/* Actions */}
      <div className="flex items-center justify-between">
        {vehicle && onDelete && (
          <Button
            type="button"
            onClick={() => onDelete(vehicle.id)}
            color="red"
          >
            Delete
          </Button>
        )}
        <div className="flex justify-end gap-3 ml-auto">
          <Button type="button" onClick={onCancel} plain>
            Cancel
          </Button>
          <Button type="submit">{vehicle ? 'Update' : 'Create'} Vehicle</Button>
        </div>
      </div>
    </form>
  );
};
